import type { ServiceLogger } from './Logger';
import { logger } from './Logger';
import { requestInterceptor } from './RequestInterceptor';
import { ServiceError } from './ServiceError';
import type {
  CacheEntry,
  CacheOptions,
  QueryOptions,
  ServiceConfig,
  ServiceResponse,
} from './ServiceTypes';

import { devAuthReady, supabase } from '@/integrations/supabase/client';
import { resolveActiveBrandKitId } from '@/lib/identity/resolveActiveBrandKitId';
import { hasActiveSession, isDevMockAuth } from '@/utils/authCheck';
import { AuthRecovery } from '@/utils/authRecovery';
import { handleInvalidSessionError } from '@/utils/invalidSessionGuard';

// Runtime-safe env access. The data services run in BOTH the browser (Vite,
// import.meta.env defined) AND Node (server routes + Workflow DevKit steps,
// where import.meta has no `env`). Reading `import.meta.env.DEV` directly threw
// "Cannot read properties of undefined (reading 'DEV')" and crashed every
// workflow step that touched a service (BUG-33 saveResult/finalizePlan).
const svcReadEnv = (key: string): string | undefined => {
  try {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    if (env && env[key] !== undefined) return env[key];
  } catch {
    /* import.meta unsupported in this runtime */
  }
  if (typeof process !== 'undefined' && process.env) return process.env[key];
  return undefined;
};
const SVC_IS_DEV = (() => {
  try {
    const env = (import.meta as unknown as { env?: { DEV?: boolean } }).env;
    if (env?.DEV !== undefined) return Boolean(env.DEV);
  } catch {
    /* fall through */
  }
  return process.env?.NODE_ENV !== 'production';
})();

export abstract class BaseService {
  protected static cacheStore = new Map<string, CacheEntry<unknown>>();
  protected static pendingRequests = new Map<string, Promise<unknown>>();
  protected static readonly MAX_CACHE_SIZE = 100;
  protected static readonly MAX_CACHE_MEMORY_MB = 50;

  protected log: ServiceLogger;

  protected config: ServiceConfig = {
    defaultTTL: 5 * 60 * 1000, // 5 minutes
    enableCache: true,
    enableRetry: true,
    maxRetries: 3,
    retryDelay: 1000,
  };

  constructor(config?: ServiceConfig) {
    if (config) {
      this.config = { ...this.config, ...config };
    }
    this.log = logger.createServiceLogger(this.constructor.name);
  }

  /**
   * Get the Supabase client instance
   */
  protected get client() {
    return supabase;
  }

  protected get supabase() {
    return supabase;
  }

  /**
   * Execute a service method with error handling and request interception
   */
  protected async execute<T>(
    operation: () => Promise<T>,
    errorMessage?: string,
    interceptorKey?: string,
  ): Promise<ServiceResponse<T>> {
    // Auto-retry on supabase-js auth-lock contention. The supabase client
    // serializes auth-token refreshes with a Web Lock; when many parallel
    // queries fire on first load, a refresh in flight can be "stolen" by a
    // newer one and the older promise rejects with "Lock ... was released
    // because another request stole it". The query is otherwise fine — just
    // retry once after a short delay.
    const runWithLockRetry = async (): Promise<T> => {
      const exec = () =>
        interceptorKey ? requestInterceptor.intercept(interceptorKey, operation) : operation();
      try {
        return await exec();
      } catch (err) {
        const msg = (err as { message?: string })?.message ?? '';
        if (/Lock .* was released because another request stole it/i.test(msg)) {
          await new Promise((resolve) => setTimeout(resolve, 150));
          return await exec();
        }
        throw err;
      }
    };

    try {
      const data = await runWithLockRetry();
      return { data, error: null };
    } catch (error) {
      if (isDevMockAuth()) {
        const serviceError = error.code
          ? ServiceError.fromPostgrestError(error)
          : ServiceError.fromError(error);
        return { data: null, error: serviceError };
      }

      const serviceError = error.code
        ? ServiceError.fromPostgrestError(error)
        : ServiceError.fromError(error);

      // Self-heal a stale/unverifiable persisted session (PGRST301). Without
      // this, a revoked-signing-key token replays on every request and bricks
      // the app with a silent 401 storm. Fire-and-forget; no-ops unless a
      // session is actually stored AND the error is a JWT-decode failure.
      void handleInvalidSessionError(error);

      // Client-side failure conditions — expected, user-facing, NOT Sentry
      // signal. Previously only checked HTTP status codes and PostgREST
      // client error prefixes. Now also treats our own ServiceError codes
      // (UNAUTHORIZED, NOT_FOUND, FORBIDDEN) as client errors so pages like
      // /auth that trigger downstream service calls pre-login don't flood
      // Sentry with "Service operation failed" / "Failed to get filtered
      // posts" noise — those are expected 401s when there's no session.
      const clientCodes = new Set(['UNAUTHORIZED', 'FORBIDDEN', 'NOT_FOUND']);
      const isClientError =
        error.code?.startsWith('PGRST1') ||
        error.status === 401 ||
        error.status === 403 ||
        error.status === 404 ||
        clientCodes.has(error.code) ||
        clientCodes.has(serviceError.code);

      if (isClientError) {
        // debug (not warn) — expected when the caller is mid-auth-handshake
        // or the user is signed-out on a public route. Upgraded to warn
        // only if it becomes a visible pattern.
        this.log.debug(errorMessage || 'Service operation failed (client error)', {
          method: 'execute',
          interceptorKey,
          error: serviceError,
        });
      } else {
        this.log.error(errorMessage || 'Service operation failed', serviceError, {
          method: 'execute',
          interceptorKey,
          originalError: error,
        });
      }

      return {
        data: null,
        error: serviceError,
      };
    }
  }

  protected createError(message: string, code: string, originalError?: unknown): ServiceError {
    return new ServiceError(
      code,
      message,
      undefined,
      originalError instanceof Error ? originalError : undefined,
    );
  }

  /**
   * Execute with retry logic
   */
  protected async executeWithRetry<T>(
    operation: () => Promise<T>,
    retries = this.config.maxRetries || 3,
  ): Promise<T> {
    let lastError: unknown;

    for (let i = 0; i < retries; i++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;

        if (i < retries - 1) {
          await new Promise((resolve) =>
            setTimeout(resolve, this.config.retryDelay || 1000 * Math.pow(2, i)),
          );
        }
      }
    }

    throw lastError;
  }

  /**
   * Get data from cache if available and not expired
   */
  protected getFromCache<T>(key: string): T | null {
    if (!this.config.enableCache) return null;

    const entry = BaseService.cacheStore.get(key);

    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      BaseService.cacheStore.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Set data in cache
   */
  protected setCache<T>(key: string, data: T, ttl?: number): void {
    if (!this.config.enableCache) return;

    // Check cache size limit
    if (BaseService.cacheStore.size >= BaseService.MAX_CACHE_SIZE) {
      // Remove oldest entry (LRU eviction)
      const firstKey = BaseService.cacheStore.keys().next().value;
      if (firstKey) {
        BaseService.cacheStore.delete(firstKey);
        this.log.debug('Cache size limit reached, evicted oldest entry', {
          evictedKey: firstKey,
          cacheSize: BaseService.cacheStore.size,
        });
      }
    }

    BaseService.cacheStore.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.defaultTTL || 5 * 60 * 1000,
    });
  }

  /**
   * Clear cache for a specific key or all cache
   */
  protected clearCache(key?: string): void {
    if (key) {
      BaseService.cacheStore.delete(key);
    } else {
      BaseService.cacheStore.clear();
    }
  }

  /**
   * Get access to the cache store for advanced operations
   */
  protected getCacheStore(): Map<string, CacheEntry<unknown>> {
    return BaseService.cacheStore;
  }

  /**
   * Invalidate cache entries by pattern
   */
  protected invalidateCacheByPattern(pattern: string): void {
    for (const [key] of BaseService.cacheStore.entries()) {
      if (key.includes(pattern)) {
        BaseService.cacheStore.delete(key);
      }
    }
  }

  /**
   * Execute with cache and optional request interception
   */
  protected async executeWithCache<T>(
    key: string,
    operation: () => Promise<T>,
    options?: CacheOptions & { useInterceptor?: boolean },
  ): Promise<T> {
    // Force refresh if requested
    if (options?.forceRefresh) {
      this.clearCache(key);
    }

    // Check cache first
    const cached = this.getFromCache<T>(key);
    if (cached !== null) {
      return cached;
    }

    // Check if there's already a pending request
    const pendingRequest = BaseService.pendingRequests.get(key);
    if (pendingRequest) {
      return pendingRequest as Promise<T>;
    }

    // Execute the operation with optional interceptor
    const promise = options?.useInterceptor
      ? requestInterceptor.intercept(key, operation)
      : operation();

    BaseService.pendingRequests.set(key, promise as Promise<unknown>);

    try {
      const result = await promise;
      this.setCache(key, result, options?.ttl);
      return result;
    } finally {
      BaseService.pendingRequests.delete(key);
    }
  }

  /**
   * Build query with common options
   */
  protected buildQuery<T>(query: T, options?: QueryOptions): T {
    let builtQuery = query as any;

    // Apply filters
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined) {
          builtQuery = builtQuery.eq(key, value);
        }
      });
    }

    // Apply ordering
    if (options?.orderBy) {
      builtQuery = builtQuery.order(options.orderBy, {
        ascending: options.orderDirection !== 'desc',
      });
    }

    // Apply pagination
    if (options?.page !== undefined && options?.limit !== undefined) {
      const from = options.page * options.limit;
      const to = from + options.limit - 1;
      builtQuery = builtQuery.range(from, to);
    }

    return builtQuery as T;
  }

  /**
   * Get current user ID
   */
  protected async getCurrentUserId(): Promise<string | null> {
    try {
      if (SVC_IS_DEV) {
        await devAuthReady;
      }

      // Use getSession instead of getUser to avoid lock contention on concurrent queries
      // The session is already cached in memory by the Supabase client
      const {
        data: { session },
        error,
      } = await this.client.auth.getSession();

      if (error || !session?.user) {
        return null;
      }

      return session.user.id;
    } catch (error) {
      this.log.debug('Failed to get current user', error);
      return null;
    }
  }

  /**
   * Get current user object
   */
  protected async getCurrentUser() {
    try {
      // Use getSession instead of getUser to avoid lock contention
      const {
        data: { session },
        error,
      } = await this.client.auth.getSession();

      // Handle auth errors gracefully
      if (error || !session?.user) {
        return null;
      }

      return session.user;
    } catch (error) {
      // Log but don't throw - return null for unauthenticated state
      this.log.debug('Failed to get current user object', error);
      return null;
    }
  }

  /**
   * Ensure user is authenticated with enhanced recovery capabilities
   */
  protected async ensureAuthenticated(): Promise<string> {
    try {
      if (SVC_IS_DEV) {
        await devAuthReady;
      }

      if (isDevMockAuth()) {
        let {
          data: { user },
        } = await this.client.auth.getUser();

        const apiUrl = svcReadEnv('VITE_SUPABASE_URL') || '';
        const isLocalStack =
          apiUrl.includes('127.0.0.1') || apiUrl.includes('localhost') || SVC_IS_DEV;
        if (!user && isLocalStack) {
          const { error: signError } = await this.client.auth.signInWithPassword({
            email: 'test@example.com',
            password: 'password123',
          });
          if (!signError) {
            ({
              data: { user },
            } = await this.client.auth.getUser());
          }
        }

        if (user?.id) {
          return user.id;
        }
      }

      const hasSession = hasActiveSession();

      if (hasSession) {
        const userId = await this.getCurrentUserId();
        if (userId) {
          this.log.debug('Standard authentication successful', {
            method: 'ensureAuthenticated',
            userId: userId.substring(0, 8) + '...',
            timestamp: new Date().toISOString(),
          });
          return userId;
        }
      }

      // debug, NOT warn — recovery is the normal retry path when the
      // session cookie is stale on first page-load, and it usually succeeds
      // within ~200ms. Logging this at warn sent every retry to Sentry as
      // an ongoing "issue" (JAVASCRIPT-REACT-87, 85 events across 8 days),
      // drowning out actual failures. If recovery itself fails, the catch
      // block below still logs at warn — that's the real signal.
      this.log.debug('Standard authentication failed, attempting recovery...', {
        method: 'ensureAuthenticated',
        hasSession,
        timestamp: new Date().toISOString(),
      });

      const userId = await AuthRecovery.ensureAuthenticatedWithRecovery();

      this.log.info('Authentication recovery successful', {
        method: 'ensureAuthenticated',
        userId: userId.substring(0, 8) + '...',
        timestamp: new Date().toISOString(),
      });

      return userId;
    } catch (error: any) {
      this.log.warn('All authentication methods failed', {
        method: 'ensureAuthenticated',
        timestamp: new Date().toISOString(),
        errorMessage: error.message,
        error,
      });

      throw ServiceError.unauthorized(
        error.message || 'You are not authorized to perform this action',
      );
    }
  }

  /**
   * The tenant id for OWNER-SCOPED tables — those whose RLS predicate is
   * `user_id = current_account_owner_id()`: media, media_collections, social_posts, projects,
   * project_tasks, brand_kits, brand_anchors, faces, user_brand_voices, voice_profiles,
   * user_style_presets, competitors, plan_documents, campaign_documents, content_plans,
   * content_batches, post_media, user_connections.
   *
   * For a solo user this returns their own id. For a team member it returns the ACCOUNT
   * OWNER's id. Writing one of those tables with the raw JWT id fails the RLS `WITH CHECK`
   * outright — or, if it goes through a service_role path that bypasses RLS, lands a row that
   * is permanently invisible to the member AND the owner. That is BUG-INTEGRATIONS-TEAM-001.
   *
   * DO NOT use this for RAW-UID tables (board_ideas, bookmarked_posts, user_ai_memory,
   * hidden_workflow_templates, visual_templates, ai_sessions, chat_sessions, ai_messages).
   * Their predicate is `user_id = auth.uid()`, so stamping the owner id is rejected for a
   * member. Check the table's live predicate first — this is not a find/replace.
   */
  protected async ensureAuthenticatedOwner(): Promise<string> {
    await this.ensureAuthenticated();
    const { data, error } = await this.client.rpc('current_account_owner_id');
    if (error) {
      // Fail closed. Falling back to the raw caller id is exactly how rows get orphaned.
      throw ServiceError.unauthorized(`Account owner lookup failed: ${error.message}`);
    }
    if (typeof data !== 'string' || !data) {
      // A null success response is not a valid solo-account fallback: using the
      // raw JWT id here would violate owner-scoped RLS for team members and can
      // create permanently split tenant data through service-role callers.
      throw ServiceError.unauthorized('Account owner lookup returned no owner');
    }
    return data;
  }

  /**
   * Resolves the active brand kit ID for the current workspace context.
   * Returns null if no active kit is configured.
   */
  protected async resolveActiveBrandKitId(): Promise<string | null> {
    return resolveActiveBrandKitId();
  }

  /**
   * Clean up expired cache entries periodically
   */
  static startCacheCleanup(interval = 60000): void {
    setInterval(() => {
      const now = Date.now();
      for (const [key, entry] of BaseService.cacheStore.entries()) {
        if (now - entry.timestamp > entry.ttl) {
          BaseService.cacheStore.delete(key);
        }
      }
    }, interval);
  }
}

// Start cache cleanup on module load
BaseService.startCacheCleanup();
